import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { RawHtml } from '@unisporkal/utilities';
import { ctaTrack } from './tracking';
import styles from './CarouselCard.module.scss';

const ImageHelper = ({ className, image, alt }) =>
  image ? (
    <img
      src={image}
      className={cx(className, 'swiper-lazy')}
      data-src={image}
      alt={alt}
    />
  ) : null;

ImageHelper.propTypes = {
  className: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
};

const H3 = ({ title = '' }) =>
  title ? (
    <h3 className={styles.cardTitle}>
      <RawHtml>{title}</RawHtml>
    </h3>
  ) : null;

H3.propTypes = { title: PropTypes.string };

const handleCtaTracking = (tracking) => {
  ctaTrack(tracking);
};

const CallToActionButton = ({
  openInNewTab = false,
  ctaLabel = '',
  ctaLink = '',
  tracking = '',
}) => {
  const isNewTab =
    typeof openInNewTab === 'string'
      ? openInNewTab.toLowerCase() === 'true'
      : openInNewTab;

  const { target, rel } = isNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : { target: '_self', rel: null };

  return (
    ctaLabel &&
    ctaLink && (
      <a
        className={styles.cardCtaLink}
        href={ctaLink}
        {...{ target, rel }}
        onClick={() => handleCtaTracking(tracking)}
      >
        {ctaLabel}
      </a>
    )
  );
};
CallToActionButton.propTypes = {
  openInNewTab: PropTypes.bool,
  tracking: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaLink: PropTypes.string,
};

const CarouselCard = ({
  title = '',
  subTitle = '',
  ctaLabel = '',
  ctaLink = '',
  backgroundImage = '',
  backgroundVideo = null,
  headerImage = null,
  titleImage = null,
  theme = 'light',
  openInNewTab = false,
  tracking = '',
}) => (
  <figure
    data-testid="carousel-card"
    className={cx(styles.cardContainer, styles[theme])}
  >
    {backgroundVideo ? (
      <video
        className={cx(styles.cardVideo, 'swiper-lazy')}
        poster={backgroundImage}
        data-src={backgroundVideo}
        autoPlay
        muted
        playsInline
        loop
        data-testid="card-video"
      />
    ) : (
      <ImageHelper
        className={styles.cardImage}
        image={backgroundImage}
        alt={title}
      />
    )}
    <figcaption className={styles.cardContent}>
      <div className={styles.cardContentGroup}>
        {headerImage && (
          <ImageHelper
            className={styles.cardHeaderImage}
            image={headerImage}
            alt=""
          />
        )}
        {titleImage ? (
          <ImageHelper
            className={styles.cardTitleImage}
            image={titleImage}
            alt={subTitle}
          />
        ) : (
          <H3 title={title} />
        )}

        {subTitle && (
          <div className={styles.cardSubTitle}>
            <RawHtml>{subTitle}</RawHtml>
          </div>
        )}
      </div>

      <CallToActionButton
        {...{
          openInNewTab,
          ctaLabel,
          ctaLink,
          tracking,
        }}
      />
    </figcaption>
  </figure>
);

CarouselCard.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaLink: PropTypes.string,
  backgroundImage: PropTypes.string,
  backgroundVideo: PropTypes.string,
  headerImage: PropTypes.string,
  titleImage: PropTypes.string,
  theme: PropTypes.string,
  openInNewTab: PropTypes.bool,
  tracking: PropTypes.string,
};

export default CarouselCard;
